'use client'

import React from 'react'
import classNames from 'classnames'
import { Header } from '@/ui/components/Header'
import { Label } from '@/ui/atoms/Label'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import Link from 'next/link'
import RichText from '@/ui/components/RichText'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

export type EcosystemCardData = {
  status: 'live' | 'planned'
  statusLabel?: string
  title: string
  description: SerializedEditorState
  features: string[]
  buttonLabel?: string
  buttonHref?: string
  buttonDisabled?: boolean
  isLive?: boolean
  alwaysInteractive?: boolean
}

interface KnowledgeEcosystemProps {
  label?: string
  title?: string
  subtitle?: SerializedEditorState
  cards: EcosystemCardData[]
  className?: string
}

export const KnowledgeEcosystem: React.FC<KnowledgeEcosystemProps> = ({
  label,
  title,
  subtitle,
  cards,
  className,
}) => {
  return (
    <div className={classNames('w-full', className)}>
      {/* Header Section */}
      <Header label={label} title={title} description={subtitle} />

      {/* Cards Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {cards.map((card, index) => (
          <EcosystemCard key={index} {...card} />
        ))}
      </div>
    </div>
  )
}

const EcosystemCard: React.FC<EcosystemCardData> = ({
  statusLabel,
  title,
  description,
  features,
  buttonLabel,
  buttonHref,
  buttonDisabled = false,
  isLive = false,
  alwaysInteractive = false,
}) => {
  return (
    <div
      className={classNames(
        'group flex flex-col border border-border-subtle rounded-lg p-6 h-full',
        isLive ? 'bg-blue-97' : 'bg-bg-base',
      )}
    >
      {/* Status Label */}
      <div className="mb-4 min-h-[24px]">
        {statusLabel && (
          <Label title={statusLabel} variant={isLive ? 'blue' : 'light-blue'} size="small" />
        )}
      </div>

      {/* Title and Description */}
      <h3 className="text-headline-S-semibold !font-bold text-text-base mb-2">{title}</h3>
      <div className="mb-6">
        <RichText
          data={description}
          className="text-body-S-semibold text-text-base prose-p:!text-body-S-semibold prose-p:!text-text-base prose-p:!mb-0"
          enableGutter={false}
          enableProse={false}
        />
      </div>

      {/* Features List */}
      <ul className="space-y-3 mb-6 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2.5">
            <span className="text-blue-40 text-body-S-regular">•</span>
            <span className="text-body-S-regular text-text-base flex-1">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Button */}
      <div className="mt-auto">
        {buttonDisabled ? (
          <Button variant="secondary" size="md" disabled className="cursor-default hover:!opacity-100 hover:!bg-neutral-100">
            <ButtonContent label={buttonLabel} />
          </Button>
        ) : buttonHref && isLive ? (
          <Button asChild variant="primary" size="md">
            <Link href={buttonHref}>
              <ButtonContent label={buttonLabel} />
            </Link>
          </Button>
        ) : buttonHref && alwaysInteractive ? (
          <Button asChild variant="secondary" size="md">
            <Link href={buttonHref}>
              <ButtonContent label={buttonLabel} />
            </Link>
          </Button>
        ) : buttonHref ? (
          <Button asChild variant="secondary" size="md" className="cursor-pointer hover:!opacity-100 hover:!bg-neutral-100">
            <Link href={buttonHref}>
              <ButtonContent label={buttonLabel} />
            </Link>
          </Button>
        ) : (
          <Button variant="secondary" size="md" disabled className="cursor-default hover:!opacity-100 hover:!bg-neutral-100">
            <ButtonContent label={buttonLabel} />
          </Button>
        )}
      </div>
    </div>
  )
}
